import React, {useState} from "react";
import {useObserver} from "mobx-react-lite";
import {useTranslation} from "react-i18next";
import {Table, Space, Popconfirm, Row, Col} from "antd";
import {PlusOutlined, EditOutlined, DeleteOutlined} from "@ant-design/icons";
import {Link} from "react-router-dom";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {EmptyMessage} from "../../_defaultComponents/EmptyMessage/EmptyMessage";
import {useFetch} from "../../../hooks/useFetch";
import stores from "../../../store/stores";
import {CreateZoneManagementModal} from "../modals/CreateZoneManagementModal";
import {UpdateZoneManagementModal} from "../modals/UpdateZoneManagementModal";

const ZoneManagement = ({isActive}) => {
    const [recallZoneManagement, setRecallZoneManagement] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [selectedZoneManagement, setSelectedZoneManagement] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const {t} = useTranslation(["common"]);

    // Zone Management (Bölge Müdürlükleri) Hook
    const [zoneManagementLoading, zoneManagementData] = useFetch({
        defaultDataValue: {},
        fetchFn: stores.operationOrganizationStore.getZoneManagementList.bind(null, {
            skip: (currentPage - 1) * pageSize,
            limit: pageSize,
            paginate: true
        }),
        condition: isActive,
        dependencies: [recallZoneManagement, isActive, currentPage, pageSize]
    });

    // Zone Management İşlemleri
    const handleCreateZoneManagement = () => {
        setCreateModalVisible(true);
    };

    const handleEditZoneManagement = record => {
        setSelectedZoneManagement(record);
        setEditModalVisible(true);
    };

    const handleCloseCreateModal = () => {
        setCreateModalVisible(false);
    };

    const handleCloseEditModal = () => {
        setEditModalVisible(false);
        setSelectedZoneManagement(null);
    };

    const handleDeleteZoneManagement = async record => {
        const result = await stores.operationOrganizationStore.deleteZoneManagement({
            id: record._id
        });
        if (result) {
            setRecallZoneManagement(!recallZoneManagement);
        }
    };

    // Table pagination change handler
    const handleTableChange = pagination => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    return useObserver(() => (
        <React.Fragment>
            <CreateZoneManagementModal
                visibility={createModalVisible}
                onCancel={handleCloseCreateModal}
                setRecallZoneManagement={setRecallZoneManagement}
            />

            <UpdateZoneManagementModal
                visibility={editModalVisible}
                detail={selectedZoneManagement}
                onCancel={handleCloseEditModal}
                setRecallZoneManagement={setRecallZoneManagement}
            />

            <Row type="flex" justify="end" style={{marginBottom: 16}}>
                <Col>
                    <CustomButton onClick={handleCreateZoneManagement} icon={<PlusOutlined />}>
                        Yeni Bölge Müdürlüğü
                    </CustomButton>
                </Col>
            </Row>

            <Table
                size="small"
                rowKey="_id"
                loading={zoneManagementLoading}
                dataSource={Array.isArray(zoneManagementData?.data) ? zoneManagementData.data : []}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                scroll={{x: "max-content"}}
                onChange={handleTableChange}
                pagination={{
                    hideOnSinglePage: true,
                    current: currentPage,
                    pageSize: pageSize,
                    total: zoneManagementData?.dataCount || 0,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
                    pageSizeOptions: ["10", "20", "50", "100"]
                }}
            >
                <Table.Column
                    dataIndex="name"
                    title="Bölge Müdürlüğü Adı"
                    render={name => <strong>{name}</strong>}
                />

                <Table.Column
                    dataIndex="bmUser"
                    title="Bölge Müdürü"
                    render={bmUser => {
                        if (!bmUser) return "-";

                        return (
                            <Link
                                target="_blank"
                                to={`/users/${bmUser._id}`}
                                style={{color: "#1890ff"}}
                            >
                                {bmUser.fullName || bmUser.username}
                            </Link>
                        );
                    }}
                />

                <Table.Column
                    align="right"
                    title={t("actions.action", {ns: "common"})}
                    render={item => (
                        <Space>
                            <CustomButton
                                size="small"
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => handleEditZoneManagement(item)}
                            >
                                Düzenle
                            </CustomButton>
                            <Popconfirm
                                title="Bu bölge müdürlüğünü silmek istediğinizden emin misiniz?"
                                onConfirm={() => handleDeleteZoneManagement(item)}
                                okText="Evet"
                                cancelText="Hayır"
                            >
                                <CustomButton
                                    size="small"
                                    type="link"
                                    danger
                                    icon={<DeleteOutlined />}
                                >
                                    Sil
                                </CustomButton>
                            </Popconfirm>
                        </Space>
                    )}
                />
            </Table>
        </React.Fragment>
    ));
};

export default ZoneManagement;
